import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { hasAdminAccess } from '@wineryxplorer/shared';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedAdminRoute component enforces role-based access control for admin routes.
 * Only users with SITE_ADMIN or SUPER_ADMIN roles can access protected routes.
 * Unauthorized users are redirected to the home page.
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5
 */
export const ProtectedAdminRoute = ({ children }: ProtectedAdminRouteProps) => {
  const { user, isLoading, isAuthenticated } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/" replace />;
  }

  // Redirect if user doesn't have admin access (SITE_ADMIN or SUPER_ADMIN)
  if (!hasAdminAccess(user.role)) {
    return <Navigate to="/" replace />;
  }

  // User is authenticated and has admin access, render children
  return <>{children}</>;
};
